import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTag from '@components/CustomTag';
import ParametrizedDatePicker from '@components/DatePicker';
import { Label } from '@components/UI';
import { UnitType } from '@pages/Financial/ContractsMoT/NewContractModal/NewContractSteps/ConfigStep';
import { bytesFormat, calculateReadableValue } from '@utils/formatters';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Tooltip,
  Skeleton,
  Divider,
  Alert,
} from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ConsumptionType = 'B' | 'KB' | 'MB';

interface IConsumption {
  value: number;
  type: ConsumptionType;
}
type Flags = 'S' | 'N';
export interface IConfigStepValues {
  description: string | undefined;
  activationDate: string | undefined;
  nbPreActivationDays: number | undefined;
  minimumConsumption: number;
  tradeInMonths: boolean | number | undefined;
  blockedByDefault: boolean;
  price: number | undefined;
  activationPrice: number | undefined;
  megabyteExcPrice: number | undefined;
  loyaltyType: number;
  loyaltyTime: number;
  penalty: number | undefined;
  automaticBlocking: boolean;
  franchise: string;
  motOperators: { name: string };
  sharingType: string;
  payPerUseFlag: Flags;
  chargeIscaOnTransfer: boolean;
  iscaDurationInDays: string | undefined;
  iscaMaxRenewal: number | undefined;
  automaticRenewal: boolean;
  planType: string;
  planExpirationMonths: string | undefined;
}

interface IConfigStep {
  onChange: (values: IConfigStepValues) => void;
  payPerUseFlag?: Flags;
  operatorDiffChargeFlag?: Flags;
  isLoading: boolean;
  defaultContractId?: number;
  contractDetails: any;
  execPriceUnit: UnitType;
  setExecPriceUnit: (value: UnitType) => void;
}

const ConfigStep = ({
  onChange,
  payPerUseFlag,
  operatorDiffChargeFlag,
  isLoading,
  defaultContractId,
  contractDetails,
  execPriceUnit,
  setExecPriceUnit,
}: IConfigStep) => {
  const { t } = useTranslation(['requests', 'attributes']);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [consumption, setConsumption] = useState<IConsumption>({
    value: 1,
    type: 'B',
  });
  const hasPreActivationDays = formValues?.nbPreActivationDays > 0;
  const loyaltyType = Form.useWatch('loyaltyType', form);
  const planType = useMemo(() => contractDetails?.planType, [contractDetails]);
  const nbPreActivationDaysValue = Form.useWatch('nbPreActivationDays', form);

  const isSharedContract = contractDetails?.sharingType === 2;

  const formInitialValues = useMemo(
    () => ({
      description: undefined,
      activationDate: undefined,
      nbPreActivationDays: undefined,
      minimumConsumption: 1,
      tradeInMonths: undefined,
      blockedByDefault: contractDetails?.franchise === 0,
      price: contractDetails?.price || 0,
      activationPrice: contractDetails?.activationPrice || 0,
      megabyteExcPrice: contractDetails?.megabyteExcPrice || 0,
      loyaltyType: contractDetails?.loyaltyType || 0,
      loyaltyTime: contractDetails?.loyaltyTime || undefined,
      penalty: contractDetails?.penalty || 0,
      automaticBlocking: contractDetails?.automaticBlocking || false,
      chargeIscaOnTransfer: contractDetails?.chargeIscaOnTransfer || false,
      iscaDurationInDays: contractDetails?.iscaDurationInDays || undefined,
      iscaMaxRenewal: contractDetails?.iscaMaxRenewal,
      automaticRenewal: contractDetails?.automaticRenewal || false,
      planType: contractDetails?.planType,
      planExpirationMonths: contractDetails?.planExpirationMonths,
    }),
    [contractDetails],
  );

  const consumptionInBytes = useMemo(() => {
    const { value, type } = consumption;
    let valueInNumber = value;

    if (type === 'KB') {
      valueInNumber *= 1024;
    }
    if (type === 'MB') {
      valueInNumber *= 1024 * 1024;
    }
    return valueInNumber;
  }, [consumption]);

  useEffect(() => {
    onChange({
      ...formValues,
      activationDate: formValues?.activationDate?.format('yyyy-MM-DD'),
      minimumConsumption: consumptionInBytes,
    });
  }, [formValues, onChange, consumptionInBytes]);

  const mapDayOptions = useCallback(
    (count: number) => {
      const label = t(`attributes:day${count > 1 ? '_plural' : ''}`, { count });
      return { label, value: count };
    },
    [t],
  );

  const nbPreActivationDaysOptions = useMemo(() => {
    const numOptions = [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30, 45, 60, 90,
    ].map(count => mapDayOptions(count));
    return [
      { label: t('attributes:noPreActivation'), value: 0 },
      ...numOptions,
    ];
  }, [t, mapDayOptions]);

  const penaltyForCancellationOptions = useMemo(
    () => [
      {
        label: t('attributes:typesOfPenalty.noPenalty'),
        value: 0,
      },
      {
        label: t('attributes:typesOfPenalty.fixedPenalty'),
        value: 1,
      },
      {
        label: t('attributes:typesOfPenalty.penaltyOnContract'),
        value: 2,
      },
    ],
    [t],
  );

  const DescriptionModel = useCallback(
    ({ label, text }: any) => (
      <CustomTag
        style={{
          margin: 4,
          padding: '2px 8px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
          {`${label}: `}
        </span>
        <span style={{ fontSize: '0.85rem', marginLeft: 4 }}>{text}</span>
      </CustomTag>
    ),
    [],
  );

  const contractTypeFormatted = useMemo(() => {
    if (contractDetails?.payPerUseFlag === 'S') {
      return t('attributes:type.payPerUse');
    }

    switch (contractDetails?.sharingType) {
      case 1:
        return t('attributes:type.sharedWithout');
      case 2:
        return t('attributes:type.sharedTotal');
      case 3:
        return t('attributes:type.sharedMixed');
      default:
        return '';
    }
  }, [contractDetails, t]);

  return (
    <Form form={form} initialValues={formInitialValues} layout="vertical">
      <Col lg={24} xl={24} xs={24}>
        <Form.Item
          required
          label={t('attributes:description')}
          name="description"
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Row gutter={[16, 16]}>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item
            label={
              <>
                {t('attributes:activationDate')}
                <Tooltip title={t('activationDateInfo')}>
                  <Button
                    type="link"
                    size="small"
                    icon={<InfoCircleOutlined />}
                  />
                </Tooltip>
              </>
            }
            name="activationDate"
          >
            <ParametrizedDatePicker
              style={{ width: '100%' }}
              disabledDate={(d: any) => d && d > moment().endOf('day')}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item
            label={t('attributes:nbPreActivationDays')}
            name="nbPreActivationDays"
          >
            <Select
              options={nbPreActivationDaysOptions}
              style={{ width: '100%' }}
              allowClear
              showSearch
            />
          </Form.Item>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          display: 'flex',
          alignItems: `flex-${hasPreActivationDays ? 'start' : 'end'}`,
        }}
      >
        {hasPreActivationDays && (
          <Col
            lg={12}
            xl={12}
            xs={24}
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Label
              style={{
                marginBottom: 8,
              }}
            >
              {t('attributes:minimumConsumption') as string}
            </Label>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              value={consumption?.value}
              onChange={value => {
                setConsumption(oldConsumption => ({
                  ...oldConsumption,
                  value: value ?? 1,
                }));
              }}
              addonAfter={
                <Select
                  style={{ minWidth: 80 }}
                  value={consumption?.type}
                  options={['', 'K', 'M'].map(item => ({
                    label: `${item}B`,
                    value: `${item}B`,
                  }))}
                  onChange={(type: ConsumptionType) => {
                    setConsumption(oldConsumption => ({
                      ...oldConsumption,
                      type,
                    }));
                  }}
                />
              }
            />
          </Col>
        )}
        <Col lg={12} xl={12} xs={24}>
          <Form.Item
            name="tradeInMonths"
            label={t('attributes:numberOfTradeInInvoices')}
          >
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col lg={12} xl={12} xs={24}>
          <Form.Item name="blockedByDefault" valuePropName="checked">
            <Checkbox>{t('attributes:blockedByDefault')}</Checkbox>
          </Form.Item>
        </Col>
        {hasPreActivationDays && (
          <Col lg={12} xl={12} xs={24}>
            <Form.Item
              name="chargeActivationFeeOnTransfer"
              valuePropName="checked"
            >
              <Checkbox>
                {t('attributes:chargeActivationFeeOnTransfer')}
                <Tooltip
                  title={t('attributes:chargeActivationFeeOnTransferInfo')}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<InfoCircleOutlined />}
                  />
                </Tooltip>
              </Checkbox>
            </Form.Item>
          </Col>
        )}
      </Row>
      {contractDetails?.franchiseBytes === 0 && (
        <Alert
          style={{ padding: 8 }}
          description={t('contracts:details.infoMessage')}
          type="warning"
          showIcon
        />
      )}
      <Skeleton
        active
        loading={isLoading}
        style={{
          marginTop: 40,
        }}
      >
        <>
          <Divider
            style={{
              marginTop: defaultContractId ? 0 : 40,
              fontSize: '0.75rem',
              opacity: 0.7,
              textTransform: 'uppercase',
            }}
          >
            {t('contracts:details.preDefined')}
          </Divider>
          <Row
            gutter={[16, 10]}
            style={{
              marginBottom: 24,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <DescriptionModel
              label={t('attributes:franchise')}
              text={bytesFormat(contractDetails?.franchise)}
            />

            {contractTypeFormatted ? (
              <DescriptionModel
                label={t('attributes:type.title')}
                text={contractTypeFormatted}
              />
            ) : undefined}

            {contractDetails?.motOperators ? (
              <DescriptionModel
                label={t('attributes:vendor')}
                text={contractDetails.motOperators.name}
              />
            ) : undefined}

            <DescriptionModel
              label={t('attributes:planType')}
              text={`${t(`attributes:planTypes.${contractDetails?.planType}`)}`}
            />

            {isSharedContract ? (
              <>
                <DescriptionModel
                  label={t('attributes:megabyteExcPrice')}
                  text={calculateReadableValue(
                    Number(contractDetails?.megabyteExcPrice),
                  )}
                />
                <DescriptionModel
                  label={t('attributes:automaticBlocking.title')}
                  text={t(
                    `attributes:automaticBlocking.${
                      contractDetails.automaticBlocking
                        ? 'activated'
                        : 'disabled'
                    }`,
                  )}
                />
              </>
            ) : undefined}
          </Row>

          <Row gutter={[16, 10]}>
            {operatorDiffChargeFlag === 'N' && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item name="price" label={t('attributes:price')}>
                  <InputNumber
                    addonBefore="R$"
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col lg={12} xl={12} xs={24}>
              <Form.Item
                name="activationPrice"
                label={t('attributes:activationPrice')}
              >
                <InputNumber
                  addonBefore="R$"
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {payPerUseFlag === 'N' && !isSharedContract && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item
                  name="megabyteExcPrice"
                  label={t('attributes:megabyteExcPrice')}
                >
                  <InputNumber
                    addonBefore="R$"
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    addonAfter={
                      <Select
                        value={execPriceUnit}
                        onChange={setExecPriceUnit}
                        options={['KB', 'MB', 'GB'].map(item => ({
                          label: item,
                          value: item,
                        }))}
                        style={{ width: 64 }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {planType === 'BAIT' && (
              <>
                <Col lg={12} xl={12} xs={24}>
                  <Form.Item
                    name="iscaDurationInDays"
                    label={t('attributes:iscaDurationInDays')}
                  >
                    <Select style={{ width: '100%' }} allowClear>
                      {[30, 60, 90].map(item => (
                        <Select.Option value={item} key={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={12} xl={12} xs={24}>
                  <Form.Item
                    name="iscaMaxRenewal"
                    label={t('attributes:iscaMaxRenewal')}
                  >
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}

            {planType === 'ALLOWANCE' && (
              <Col lg={12} xl={12} xs={24}>
                <Form.Item
                  required
                  name="planExpirationMonths"
                  label={t('attributes:planExpirationMonths')}
                >
                  <Select style={{ width: '100%' }}>
                    <Select.Option value={12}>
                      {`12 ${t('attributes:months')}`}
                    </Select.Option>
                    <Select.Option value={24}>
                      {`24 ${t('attributes:months')}`}
                    </Select.Option>
                    <Select.Option value={36}>
                      {`36 ${t('attributes:months')}`}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
            {planType !== 'ALLOWANCE' && (
              <>
                {planType !== 'BAIT' && (
                  <Col lg={12} xl={12} xs={24}>
                    <Form.Item
                      name="loyaltyType"
                      label={t('attributes:penaltyForCancellation')}
                    >
                      <Select
                        options={penaltyForCancellationOptions}
                        style={{ width: '100%' }}
                        allowClear
                        showSearch
                      />
                    </Form.Item>
                  </Col>
                )}
                {loyaltyType === 1 && (
                  <Col lg={12} xl={12} xs={24}>
                    <Form.Item
                      name="penalty"
                      label={t('attributes:penaltyAmount')}
                    >
                      <InputNumber
                        addonBefore="R$"
                        min={0}
                        step={0.01}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                )}
                {loyaltyType === 2 && (
                  <Col lg={12} xl={12} xs={24}>
                    <Form.Item
                      required
                      name="loyaltyTime"
                      label={t('attributes:loyaltyTime')}
                    >
                      <Select style={{ width: '100%' }}>
                        {[12, 24, 36].map(item => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </>
            )}
          </Row>

          <Row gutter={[16, 16]}>
            {planType === 'BAIT' && nbPreActivationDaysValue > 0 && (
              <Col sm={12} xs={24}>
                <Form.Item name="chargeIscaOnTransfer" valuePropName="checked">
                  <Checkbox>
                    {t('attributes:chargeIscaOnTransfer')}
                    <Tooltip title={t('attributes:chargeIscaOnTransferInfo')}>
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
            )}

            {planType === 'ALLOWANCE' && (
              <Col sm={12} xs={24}>
                <Form.Item
                  required
                  name="automaticRenewal"
                  valuePropName="checked"
                >
                  <Checkbox>{t('attributes:automaticRenewal')}</Checkbox>
                </Form.Item>
              </Col>
            )}

            {!isSharedContract && (
              <Col sm={12} xs={24}>
                <Form.Item name="automaticBlocking" valuePropName="checked">
                  <Checkbox>{t('attributes:automaticBlocking.title')}</Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>
        </>
      </Skeleton>
    </Form>
  );
};

export default ConfigStep;
